import React, { useCallback } from "react";
import { connect } from "react-redux";

import {
  EntitiesSupportingFiles,
  EntityTypeSupportingFiles,
} from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";

import PreviewFile from "@app/component/FilePreview/PreviewFile";
import { RootState } from "@app/store/rootReducer";

import { getFilePreview, isFilePreviewLoading } from "./store";
import { downloadFile, fetchPreview, removeFile } from "./store/actions";

interface Props {
  disabled?: boolean;
  doDownload: typeof downloadFile;
  doFetchFilePreview: typeof fetchPreview.request;
  doRemoveFile: typeof removeFile.request;
  entityId: EntitiesSupportingFiles["id"];
  entityType: EntityTypeSupportingFiles;
  file: RawFile;
  filePreview?: Blob;
  loading?: boolean;
  onHide: () => void;
  onRemove?: (file: RawFile) => void;
}

// !!! Use this component with care. It assumes that there's only one active preview at the moment.
// If you need two or more simultaneous previews, take low-level PreviewFile component and implement
// multi-preview yourself.
const FilePreview: React.FC<Props> = ({
  disabled,
  doDownload,
  doFetchFilePreview,
  doRemoveFile,
  entityId,
  entityType,
  file,
  filePreview,
  loading,
  onHide,
  onRemove,
}) => {
  const handleFetch = useCallback(
    (fileId) => {
      doFetchFilePreview({ entityId, entityType, fileId });
    },
    [doFetchFilePreview, entityId, entityType]
  );

  return (
    <PreviewFile
      disabled={disabled}
      file={file}
      loading={loading}
      onCancel={onHide}
      onDelete={(file: RawFile) => {
        doRemoveFile({ callback: onRemove, entityId, entityType, file });
        onHide();
      }}
      onDownload={() => {
        doDownload({ entityId, entityType, file });
      }}
      onFetchFilePreview={handleFetch}
      preview={filePreview}
      previewId={file.id}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  filePreview: getFilePreview(state),
  loading: isFilePreviewLoading(state),
});

const mapDispatchToProps = {
  doDownload: downloadFile,
  doFetchFilePreview: fetchPreview.request,
  doRemoveFile: removeFile.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilePreview);
